import React, { useEffect, useRef, useState } from 'react';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DrawIcon from '@mui/icons-material/Draw';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { loadAnnotationImage, renderAnnotationCanvas } from './dataExchangeAnnotationCanvas';

const EMPTY_DOCUMENT = { version: 1, width: 1, height: 1, operations: [] };

function DataExchangeAnnotationEditor({ open, imageUrl, annotation, onClose, onSave, saving = false }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const operationsRef = useRef([]);
  const draftRef = useRef(null);
  const [operations, setOperations] = useState([]);
  const [redoOperations, setRedoOperations] = useState([]);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#E31B23');
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [title, setTitle] = useState('');
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });

  const redraw = (nextOperations = operationsRef.current, draft = draftRef.current) => {
    const document = { version: 1, ...imageSize, operations: draft ? [...nextOperations, draft] : nextOperations };
    renderAnnotationCanvas(canvasRef.current, imageRef.current, document);
  };

  useEffect(() => {
    if (!open || !imageUrl) return undefined;
    let active = true;
    setTitle(annotation?.title || '');
    setRedoOperations([]);
    loadAnnotationImage(imageUrl).then((image) => {
      if (!active) return;
      imageRef.current = image;
      const size = { width: image.naturalWidth, height: image.naturalHeight };
      const initialOperations = annotation?.document?.operations || [];
      setImageSize(size);
      operationsRef.current = initialOperations;
      setOperations(initialOperations);
      renderAnnotationCanvas(canvasRef.current, image, { version: 1, ...size, operations: initialOperations });
    });
    return () => { active = false; };
  }, [annotation, imageUrl, open]);

  useEffect(() => {
    operationsRef.current = operations;
    redraw(operations, null);
  }, [operations]);

  const pointerPosition = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(imageSize.width, (event.clientX - rect.left) * imageSize.width / rect.width)),
      y: Math.max(0, Math.min(imageSize.height, (event.clientY - rect.top) * imageSize.height / rect.height)),
    };
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    const point = pointerPosition(event);
    draftRef.current = tool === 'pen'
      ? { type: tool, color, strokeWidth, points: [point, point] }
      : { type: tool, color, strokeWidth, start: point, end: point };
    redraw();
  };

  const handlePointerMove = (event) => {
    if (!draftRef.current) return;
    event.preventDefault();
    const point = pointerPosition(event);
    if (draftRef.current.type === 'pen') draftRef.current = { ...draftRef.current, points: [...draftRef.current.points, point] };
    else draftRef.current = { ...draftRef.current, end: point };
    redraw();
  };

  const handlePointerUp = (event) => {
    if (!draftRef.current) return;
    canvasRef.current.releasePointerCapture(event.pointerId);
    const completed = draftRef.current;
    draftRef.current = null;
    setOperations((current) => [...current, completed]);
    setRedoOperations([]);
  };

  const undo = () => setOperations((current) => {
    if (!current.length) return current;
    setRedoOperations((redo) => [current[current.length - 1], ...redo]);
    return current.slice(0, -1);
  });

  const redo = () => setRedoOperations((current) => {
    if (!current.length) return current;
    setOperations((drawn) => [...drawn, current[0]]);
    return current.slice(1);
  });

  const closeEditor = () => {
    const initial = annotation?.document?.operations || [];
    const changed = JSON.stringify(initial) !== JSON.stringify(operations);
    if (!changed || window.confirm('Ungespeicherte Markierungen verwerfen?')) onClose();
  };

  const save = () => onSave({
    title,
    document: { version: 1, ...imageSize, operations },
  });

  return (
    <Dialog open={open} onClose={closeEditor} fullScreen={fullScreen} maxWidth="lg" fullWidth>
      <DialogTitle>{annotation ? 'Markierung bearbeiten' : 'Foto markieren'}</DialogTitle>
      <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <ToggleButtonGroup exclusive value={tool} onChange={(event, value) => value && setTool(value)} size="small" aria-label="Markierungswerkzeug">
              <ToggleButton value="pen" aria-label="Freihandstift"><Tooltip title="Freihandstift"><DrawIcon /></Tooltip></ToggleButton>
              <ToggleButton value="arrow" aria-label="Pfeil"><Tooltip title="Pfeil"><ArrowOutwardIcon /></Tooltip></ToggleButton>
              <ToggleButton value="circle" aria-label="Kreis"><Tooltip title="Kreis"><CircleOutlinedIcon /></Tooltip></ToggleButton>
              <ToggleButton value="rectangle" aria-label="Rechteck"><Tooltip title="Rechteck"><CropSquareIcon /></Tooltip></ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box component="input" type="color" value={color} onChange={(event) => setColor(event.target.value.toUpperCase())} aria-label="Markierungsfarbe" sx={{ width: 44, height: 36, border: 0, bgcolor: 'transparent' }} />
              <Typography variant="body2">Stärke</Typography>
              <Slider value={strokeWidth} onChange={(event, value) => setStrokeWidth(value)} min={2} max={20} step={1} sx={{ width: 110 }} aria-label="Strichstärke" />
              <Tooltip title="Rückgängig"><span><IconButton onClick={undo} disabled={!operations.length}><UndoIcon /></IconButton></span></Tooltip>
              <Tooltip title="Wiederholen"><span><IconButton onClick={redo} disabled={!redoOperations.length}><RedoIcon /></IconButton></span></Tooltip>
              <Tooltip title="Alle Markierungen löschen"><span><IconButton onClick={() => { setOperations([]); setRedoOperations([]); }} disabled={!operations.length}><DeleteSweepIcon /></IconButton></span></Tooltip>
            </Stack>
          </Stack>
          <TextField size="small" label="Bezeichnung (optional)" value={title} onChange={(event) => setTitle(event.target.value)} inputProps={{ maxLength: 255 }} />
          <Box sx={{ minHeight: 300, maxHeight: '68vh', display: 'grid', placeItems: 'center', overflow: 'auto', bgcolor: '#20272d', p: 1 }}>
            <Box
              component="canvas"
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              sx={{ display: 'block', maxWidth: '100%', maxHeight: '64vh', width: 'auto', height: 'auto', touchAction: 'none', cursor: 'crosshair' }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeEditor}>Abbrechen</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || !operations.length}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DataExchangeAnnotationEditor;